import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, FileText } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';

const TermsOfService = () => {
  const lastUpdated = 'December 28, 2025';
  const { user, isAuthenticated } = useAuth();
  const userPlan = user?.subscription?.plan || 'none';
  
  // Determine navigation destinations based on auth status
  const logoDestination = isAuthenticated && (userPlan === 'standard' || userPlan === 'pro') ? '/dashboard' : '/';
  const backButtonDestination = isAuthenticated && (userPlan === 'standard' || userPlan === 'pro') ? '/dashboard' : '/';
  const backButtonText = isAuthenticated && (userPlan === 'standard' || userPlan === 'pro') ? 'Back to Dashboard' : 'Back to Home';
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to={logoDestination} className="flex items-center gap-2">
              <Shield className="h-8 w-8 text-slate-800" />
              <span className="font-heading font-bold text-xl text-slate-900">AIVORA Comply360</span>
            </Link>
            <Button variant="outline" asChild>
              <Link to={backButtonDestination}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                {backButtonText}
              </Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Title */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center p-3 bg-slate-100 rounded-2xl mb-4">
              <FileText className="h-8 w-8 text-slate-700" />
            </div>
            <h1 className="font-heading font-bold text-4xl text-slate-900 mb-2">
              Terms of Service
            </h1>
            <p className="text-slate-500">Last updated: {lastUpdated}</p>
          </div>

          {/* Content Card */}
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 md:p-12">
            <div className="prose prose-slate max-w-none">
              
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Acceptance of Terms</h2>
                <p className="text-slate-600 leading-relaxed">
                  By accessing or using AIVORA Comply360 (the Service), you agree to be bound by these Terms of Service. 
                  If you do not agree to these terms, please do not use the Service. AIVORA Comply360 is owned and operated 
                  by AIVORA Techlabs Global Pvt Ltd (we, us, or our).
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Description of Service</h2>
                <p className="text-slate-600 leading-relaxed">
                  AIVORA Comply360 is an ISO 27001:2022 Maturity Assessment platform designed to help organizations 
                  evaluate and improve their information security management systems. The Service includes:
                </p>
                <ul className="list-disc pl-6 mt-3 text-slate-600 space-y-2">
                  <li>Security maturity assessments based on ISO 27001:2022 controls</li>
                  <li>Compliance reporting and gap analysis</li>
                  <li>Team collaboration features</li>
                  <li>Audit logging and history tracking</li>
                  <li>Knowledge base and documentation resources</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Account Registration</h2>
                <p className="text-slate-600 leading-relaxed">
                  To use certain features of the Service, you must register for an account. You agree to:
                </p>
                <ul className="list-disc pl-6 mt-3 text-slate-600 space-y-2">
                  <li>Provide accurate, current, and complete information during registration</li>
                  <li>Maintain and promptly update your account information</li>
                  <li>Keep your login credentials secure and confidential</li>
                  <li>Accept responsibility for all activities that occur under your account</li>
                  <li>Notify us immediately of any unauthorized use of your account</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Subscription Plans</h2>
                <p className="text-slate-600 leading-relaxed">
                  AIVORA Comply360 offers Standard and Pro subscription plans. Each plan provides access to different 
                  features and assessment capabilities. Upon purchase, you will have immediate access to all features 
                  included in your selected plan. We reserve the right to modify pricing and features at any time.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Payment</h2>
                <p className="text-slate-600 leading-relaxed">
                  Subscriptions are available as one-time purchases. By purchasing, you authorize us to 
                  charge your payment method. Subscription fees are non-refundable except as 
                  required by law or as explicitly stated in these terms.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Acceptable Use</h2>
                <p className="text-slate-600 leading-relaxed">You agree not to:</p>
                <ul className="list-disc pl-6 mt-3 text-slate-600 space-y-2">
                  <li>Use the Service for any unlawful purpose or in violation of any laws</li>
                  <li>Attempt to gain unauthorized access to any part of the Service</li>
                  <li>Interfere with or disrupt the Service or servers connected to the Service</li>
                  <li>Share your account credentials with unauthorized parties</li>
                  <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
                  <li>Use the Service to transmit malware or other harmful code</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Intellectual Property</h2>
                <p className="text-slate-600 leading-relaxed">
                  The Service and its original content, features, and functionality are owned by AIVORA Techlabs 
                  Global Pvt Ltd and are protected by international copyright, trademark, and other intellectual 
                  property laws. You may not copy, modify, distribute, sell, or lease any part of our Service 
                  without our express written consent.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Data Ownership</h2>
                <p className="text-slate-600 leading-relaxed">
                  You retain ownership of all data you submit to the Service. By using the Service, you grant us 
                  a limited license to use, process, and store your data solely for the purpose of providing the 
                  Service to you. We will not sell or share your data with third parties except as described in 
                  our Privacy Policy.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Disclaimer of Warranties</h2>
                <p className="text-slate-600 leading-relaxed">
                  THE SERVICE IS PROVIDED AS IS AND AS AVAILABLE WITHOUT WARRANTIES OF ANY KIND, EITHER 
                  EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR 
                  ERROR-FREE. THE SERVICE IS NOT A SUBSTITUTE FOR PROFESSIONAL SECURITY CONSULTATION OR 
                  CERTIFICATION AUDITS.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Limitation of Liability</h2>
                <p className="text-slate-600 leading-relaxed">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, AIVORA TECHLABS GLOBAL PVT LTD SHALL NOT BE LIABLE FOR 
                  ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF YOUR USE 
                  OF THE SERVICE. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT PAID BY YOU FOR THE SERVICE 
                  IN THE 12 MONTHS PRECEDING THE CLAIM.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">11. Termination</h2>
                <p className="text-slate-600 leading-relaxed">
                  We may terminate or suspend your account and access to the Service immediately, without prior 
                  notice, for any breach of these Terms. Upon termination, your right to use the Service will 
                  cease immediately. You may also terminate your account at any time by contacting us.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">12. Changes to Terms</h2>
                <p className="text-slate-600 leading-relaxed">
                  We reserve the right to modify these Terms at any time. We will provide notice of significant 
                  changes by posting the new Terms on this page and updating the Last Updated date. Your 
                  continued use of the Service after such changes constitutes acceptance of the new Terms.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">13. Governing Law</h2>
                <p className="text-slate-600 leading-relaxed">
                  These Terms shall be governed by and construed in accordance with the laws of India, without 
                  regard to its conflict of law provisions. Any disputes arising from these Terms shall be 
                  subject to the exclusive jurisdiction of the courts in Bangalore, India.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">14. Contact Us</h2>
                <p className="text-slate-600 leading-relaxed">
                  If you have any questions about these Terms of Service, please contact us at:
                </p>
                <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                  <p className="text-slate-700 font-semibold">AIVORA Techlabs Global Pvt Ltd</p>
                  <p className="text-slate-600">Email: <a href="mailto:contact@aivora-techlabs.com" className="text-blue-600 hover:underline">contact@aivora-techlabs.com</a></p>
                  <p className="text-slate-600">Support: <a href="mailto:support@aivora-techlabs.com" className="text-blue-600 hover:underline">support@aivora-techlabs.com</a></p>
                </div>
              </section>

            </div>
          </div>

          {/* Back Link */}
          <div className="text-center mt-8">
            <Link to="/" className="text-slate-500 hover:text-slate-700 text-sm">
              ← Back to AIVORA Comply360
            </Link>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default TermsOfService;
